import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Check, Sparkles, Plus, Trash2, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { CurveBottom, Bamboo, Cloud } from "../components/Decor";
import { PANDA_ACCESSORIES, PANDA_SCARVES } from "../data/mock";
import { useProgress } from "../context/ProgressContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DAYS } from "../lib/lessonDay";

const CreatePanda = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { children, activeChild, addChild, updateChildPanda, updateChild, removeChild, switchChild } = useProgress();
  const { user } = useAuth();

  const isNew = params.get("new") === "1" || children.length === 0;
  const editId = params.get("child") || activeChild?.id;
  const editing = !isNew ? children.find((c) => c.id === editId) : null;

  const [name, setName] = useState("");
  const [accessory, setAccessory] = useState("none");
  const [scarf, setScarf] = useState("#e5356f");
  const [lessonDay, setLessonDay] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.panda.name);
      setAccessory(editing.panda.accessory);
      setScarf(editing.panda.scarf);
      setLessonDay(editing.lessonDay === null || editing.lessonDay === undefined ? "" : String(editing.lessonDay));
    } else {
      setName("");
      setAccessory("none");
      setScarf("#e5356f");
      setLessonDay("");
    }
  }, [editing?.id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give your panda a name first!");
      return;
    }
    const panda = { name: name.trim(), accessory, scarf };
    setBusy(true);
    try {
      const ld = lessonDay === "" ? null : Number(lessonDay);
      if (editing) {
        updateChildPanda(editing.id, panda);
        updateChild(editing.id, { lessonDay: ld });
        switchChild(editing.id);
        toast.success(`${panda.name} updated!`);
      } else {
        await addChild(panda, ld);
        toast.success(`${panda.name} joined the family!`, user ? { description: "Saved to your account." } : { description: "Create a free account to keep progress on every device.", action: { label: "Sign up", onClick: () => navigate("/login", { state: { tab: "register" } }) } });
      }
      navigate("/lessons");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Remove ${editing.panda.name} and all of their progress?`)) return;
    await removeChild(editing.id);
    toast(`${editing.panda.name} removed`);
    setParams({});
  };

  return (
    <div data-testid="create-panda-page">
      <section className="relative bg-[#6dcdf2] overflow-hidden">
        <Bamboo className="absolute -left-8 -top-10 hidden md:block" height={380} />
        <Cloud className="absolute right-[8%] top-10 hidden md:block cloud-drift" size={200} />
        <div className="relative max-w-5xl mx-auto px-6 pt-14 pb-28 text-center">
          <h1 className="text-white font-bold text-4xl sm:text-5xl drop-shadow-sm" style={{ fontFamily: "Literata, serif" }}>
            {editing ? `Update ${editing.panda.name}'s panda` : children.length ? "Add another child" : "Create your panda"}
          </h1>
          <p className="text-[#0c4a6e] text-lg mt-4 max-w-xl mx-auto font-medium">
            Every child in the family gets their own panda, bamboo and progress.
          </p>
        </div>
        <CurveBottom color="#ffffff" />
      </section>

      {children.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 -mt-4 relative z-10">
          <div className="bg-white rounded-3xl shadow-lg p-4 flex flex-wrap items-center gap-3" data-testid="children-switcher">
            <span className="text-sm font-bold text-gray-500 px-2">Family:</span>
            {children.map((c) => (
              <button
                key={c.id}
                onClick={() => setParams({ child: c.id })}
                className={`flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border-2 transition-colors ${editing?.id === c.id ? "border-[#1b8bc9] bg-[#e9f9fd]" : "border-transparent bg-[#f7fdfe] hover:bg-[#e9f9fd]"}`}
                data-testid={`child-chip-${c.id}`}
              >
                <Panda size={30} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                <span className="font-bold text-[#0c4a6e] text-sm">{c.panda.name}</span>
              </button>
            ))}
            <button onClick={() => setParams({ new: "1" })} className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold border-2 transition-colors ${isNew ? "border-[#e5356f] bg-[#fff0f5] text-[#e5356f]" : "border-dashed border-[#c9ecf5] text-[#1b8bc9] hover:bg-[#e9f9fd]"}`} data-testid="add-child-chip">
              <Plus size={16} /> Add a child
            </button>
            <Link to="/family" className="ml-auto inline-flex items-center gap-1 text-sm font-bold text-[#7d5fb2] hover:underline" data-testid="go-dashboard"><LayoutDashboard size={16} /> Family dashboard</Link>
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-12 items-start">
        <div className="flex flex-col items-center">
          <div className="relative bg-[#e9f9fd] rounded-[40px] p-8 w-full max-w-sm flex items-center justify-center shadow-inner">
            <div className="absolute inset-x-12 bottom-8 h-6 bg-[#0c4a6e]/10 rounded-full blur-md" />
            <Panda size={240} accessory={accessory} scarf={scarf} item="coin" animate />
          </div>
          <p className="mt-4 font-logo text-3xl text-[#0c4a6e]" data-testid="panda-preview-name">{name || "Your panda"}</p>
          {editing && <p className="text-sm text-[#2e8b57] font-bold mt-1">{editing.bamboo} bamboo earned so far</p>}
          {!user && (
            <p className="mt-3 text-sm text-gray-500 text-center">Progress is saved on this device. <Link to="/login" className="text-[#1b8bc9] font-bold hover:underline" data-testid="panda-login-link">Log in or sign up</Link> to sync it everywhere.</p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="panda-form">
          <div>
            <Label htmlFor="panda-name" className="text-[#0c4a6e] font-bold text-base">Child's name (or panda's name)</Label>
            <Input id="panda-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Penny" maxLength={20} className="mt-2 h-12 rounded-full px-5 text-lg border-2 border-[#c9ecf5] focus-visible:ring-[#1b8bc9]" data-testid="panda-name-input" />
          </div>

          <div>
            <p className="text-[#0c4a6e] font-bold">Pick an accessory</p>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {PANDA_ACCESSORIES.map((a) => (
                <button key={a.id} type="button" onClick={() => setAccessory(a.id)} className={`relative rounded-2xl border-2 p-2 flex flex-col items-center gap-1 transition-[border-color,background-color,transform] hover:-translate-y-0.5 ${accessory === a.id ? "border-[#e5356f] bg-[#fff0f5]" : "border-[#e9f9fd] bg-white hover:border-[#c9ecf5]"}`} data-testid={`accessory-${a.id}`}>
                  <Panda size={56} accessory={a.id} scarf={scarf} />
                  <span className="text-xs font-bold text-gray-700">{a.label}</span>
                  {accessory === a.id && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#e5356f] text-white flex items-center justify-center"><Check size={12} /></span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[#0c4a6e] font-bold">Scarf color</p>
            <div className="mt-3 flex gap-3 flex-wrap">
              {PANDA_SCARVES.map((s) => (
                <button key={s.id} type="button" aria-label={s.label} onClick={() => setScarf(s.id)} className={`w-11 h-11 rounded-full border-4 transition-transform hover:scale-110 ${scarf === s.id ? "border-[#0c4a6e] scale-110" : "border-white shadow"}`} style={{ background: s.id }} data-testid={`scarf-${s.label.toLowerCase()}`} />
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="lesson-day" className="text-[#0c4a6e] font-bold text-base">Weekly lesson day <span className="text-gray-400 font-normal text-sm">(optional)</span></Label>
            <p className="text-xs text-gray-500 mt-1">Pick a regular day - the dashboard shows a countdown and we'll nudge you on the day.</p>
            <select id="lesson-day" value={lessonDay} onChange={(e) => setLessonDay(e.target.value)} className="mt-2 h-12 w-full rounded-full border-2 border-[#c9ecf5] bg-white px-5 text-[#0c4a6e] font-bold focus:outline-none focus:border-[#1b8bc9]" data-testid="lesson-day-input">
              <option value="">No set day</option>
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button type="submit" disabled={busy} className="btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white shadow-lg inline-flex items-center gap-2 disabled:opacity-60" data-testid="save-panda-button">
              <Sparkles size={20} /> {editing ? "Save changes" : "Let's go!"}
            </button>
            {editing && (
              <button type="button" onClick={remove} className="inline-flex items-center gap-1 text-sm font-bold text-gray-500 hover:text-[#e5356f]" data-testid="remove-child-button">
                <Trash2 size={16} /> Remove {editing.panda.name}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};

export default CreatePanda;
